import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  TextField,
  Button,
  List,
  ListItem,
  Avatar,
  CircularProgress,
  Typography,
  Paper,
  IconButton,
  Collapse,
  Dialog,
  DialogContent,
  DialogActions,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CloseIcon from "@mui/icons-material/Close";
import { renderMessageWithCitations } from "./chatCitationUtils";
import {
  ChatStartConversationBody,
  ChatStartConversationResponse,
  ChatMessageResponse,
  ChatSource,
  ChatAttachmentSource,
} from "@services/chat/types";
import {
  DownloadAttachmentRequest,
  DownloadAttachmentResponse,
} from "@services/shipments/types";
import { EventLogger } from "@/utils/eventLogger";

interface ChatMessage {
  role: "user" | "dobby";
  content: string;
  timestamp: Date;
  sources?: ChatSource[];
}

interface DobbyChatFloatingProps {
  conversationType: "example" | "rule_update" | "shipment_question";
  initialMessage?: string;
  ruleId?: string;
  extraParams?: {
    feedId?: string;
  };
  onConversationEnd?: (result: string | null) => void;
  // Required: API client for making requests
  apiClient: {
    post<TResponse, TRequest = any>(
      url: string,
      data?: TRequest,
      config?: any
    ): Promise<TResponse>;
  };
  // Optional: Component for displaying email threads in modal
  EmailThreadComponent?: React.ComponentType<{
    emailId: string;
    sx?: any;
  }>;
  // Optional: Custom avatar image path
  avatarImagePath?: string;
  // Optional: Callback to scroll to an email in the timeline (instead of opening modal)
  onScrollToEmail?: (emailId: string) => void;
}

const DobbyChatFloating: React.FC<DobbyChatFloatingProps> = ({
  conversationType,
  initialMessage,
  ruleId,
  extraParams,
  onConversationEnd,
  apiClient,
  EmailThreadComponent,
  avatarImagePath = "/www/assets/dobby_internal_tools_icon.png",
  onScrollToEmail,
}) => {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversationEnded, setConversationEnded] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  // Email popup state
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    if (messagesContainerRef.current && messagesEndRef.current) {
      const container = messagesContainerRef.current;
      container.scrollTo({
        top: container.scrollHeight,
        behavior: "smooth",
      });
    }
  };

  const startConversation = useCallback(
    async (firstMessage?: string) => {
      setIsLoading(true);
      try {
        const response = await apiClient.post<ChatStartConversationResponse>(
          "/chat/conversation/start/",
          {
            conversationType: conversationType,
            initialMessage: firstMessage,
            ruleId: ruleId,
            extraParams: extraParams,
          } satisfies ChatStartConversationBody
        );

        setConversationId(response.conversationId);

        // Track conversation start
        EventLogger.getInstance().track({
          name: "chat_conversation_started",
          conversationId: response.conversationId,
          conversationType: conversationType,
          feedId: extraParams?.feedId,
          hasInitialMessage: !!firstMessage,
        });

        if (firstMessage) {
          setMessages([
            {
              role: "user",
              content: firstMessage,
              timestamp: new Date(),
            },
          ]);
        }

        if (response.dobbyResponse) {
          setMessages((prev) => [
            ...prev,
            {
              role: "dobby",
              content: response.dobbyResponse.dobbyResponse,
              timestamp: new Date(),
              sources: response.dobbyResponse.sources,
            },
          ]);
        }
      } catch (error) {
        console.error("Error starting conversation:", error);
      } finally {
        setIsLoading(false);
      }
    },
    [apiClient, conversationType, ruleId, extraParams]
  );

  // Initialize conversation if initial message is provided
  useEffect(() => {
    if (initialMessage && !conversationId) {
      startConversation(initialMessage);
    }
  }, [initialMessage, conversationId, startConversation]);

  const sendMessage = async () => {
    if (!input.trim() || isLoading || conversationEnded) return;

    const userMessage = input;
    setInput("");
    const tempMessages = [
      ...messages,
      {
        role: "user" as "user",
        content: userMessage,
        timestamp: new Date(),
      },
    ];
    setMessages(tempMessages);
    setIsLoading(true);

    try {
      // Start conversation if not already started
      if (!conversationId) {
        await startConversation(userMessage);
        return;
      }

      // Track chat message event
      EventLogger.getInstance().track({
        name: "chat_message_sent",
        conversationId: conversationId,
        message: userMessage,
        conversationType: conversationType,
        feedId: extraParams?.feedId,
      });

      // Send message to existing conversation
      const response = await apiClient.post<ChatMessageResponse>(
        `/chat/conversation/${conversationId}/chat/`,
        {
          message: userMessage,
        }
      );

      setMessages((prev) => [
        ...prev,
        {
          role: "dobby",
          content: response.dobbyResponse,
          timestamp: new Date(),
          sources: response.sources,
        },
      ]);

      // Track response received
      EventLogger.getInstance().track({
        name: "chat_response_received",
        conversationId: conversationId,
        conversationType: conversationType,
        feedId: extraParams?.feedId,
        hasResponse: !!response.dobbyResponse,
        sourcesCount: response.sources?.length || 0,
      });

      if (response.conversationEnded) {
        setConversationEnded(true);
        if (onConversationEnd) {
          onConversationEnd(response.result || null);
        }
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages((prev) => [
        ...prev,
        {
          role: "dobby",
          content: "Sorry, something went wrong. Please try again.",
          timestamp: new Date(),
        },
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  // Handle source click (scrolls to email in timeline or opens modal)
  const handleSourceClick = (source: ChatSource) => {
    const emailId = source.data.emailId;

    // Prefer scrolling to email in timeline if callback is provided
    if (onScrollToEmail) {
      onScrollToEmail(emailId);
      return;
    }

    // Fall back to opening modal if EmailThreadComponent is provided
    if (EmailThreadComponent) {
      setSelectedEmailId(emailId);
      setEmailModalOpen(true);
    }
  };

  // Handle attachment download - opens in new tab
  const handleDownloadAttachment = async (source: ChatAttachmentSource) => {
    try {
      const response = await apiClient.post<
        DownloadAttachmentResponse,
        DownloadAttachmentRequest
      >("/shipments/download-attachment", {
        emailId: source.data.emailId,
        attachmentId: source.data.attachmentId,
      });

      // Open in new tab
      window.open(response.downloadUrl, "_blank");
    } catch (error) {
      console.error("Error downloading attachment:", error);
    }
  };

  // Handle text field click to expand when collapsed
  const handleTextFieldClick = () => {
    if (isCollapsed) {
      setIsCollapsed(false);
    }
  };

  // Handle wrapper click to expand when collapsed (works even when loading)
  const handleWrapperClick = (e: React.MouseEvent) => {
    // Only expand if collapsed and clicking on the input area
    if (isCollapsed && messages.length > 0) {
      setIsCollapsed(false);
    }
  };

  // Calculate dynamic height based on messages and collapse state
  const calculateHeight = () => {
    const inputHeight = 50; // Text input area height

    if (isCollapsed || messages.length === 0) {
      return `${inputHeight}px`; // Just show text input when collapsed or no messages
    }

    return undefined;
  };

  return (
    <>
      <Paper
        className="dobby-chat-paper"
        elevation={8}
        sx={{
          position: "fixed",
          bottom: 8,
          right: 90, // Center within 780px sidebar: (780 - 600) / 2 = 90
          width: "600px",
          maxWidth: "calc(780px - 24px)",
          height: calculateHeight(),
          borderRadius: "16px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          zIndex: 1000,
          transition: "height 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        }}
      >
        {/* Close/Expand button - show when there are messages */}
        {messages.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              top: isCollapsed ? "50%" : 6,
              left: 6,
              transform: isCollapsed ? "translateY(-50%)" : "none",
              zIndex: 1001,
            }}
          >
            <IconButton
              size="small"
              onClick={() => setIsCollapsed(!isCollapsed)}
              sx={{
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                width: 24,
                height: 24,
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 1)",
                },
              }}
            >
              {isCollapsed ? (
                <Box
                  component="span"
                  sx={{
                    fontSize: 14,
                    fontWeight: "bold",
                    color: isLoading ? "primary.main" : "text.secondary",
                  }}
                >
                  {isLoading ? "..." : "↑"}
                </Box>
              ) : (
                <CloseIcon sx={{ fontSize: 16 }} />
              )}
            </IconButton>
          </Box>
        )}

        {/* Message History */}
        <Collapse in={!isCollapsed && messages.length > 0}>
          <Box
            ref={messagesContainerRef}
            sx={{
              maxHeight: "360px",
              minHeight: "40px",
              overflowY: "auto",
              p: 0.5,
              backgroundColor: "#f9f9f9",
              flex: 1,
            }}
          >
            <List dense>
              {messages.map((msg, index) => (
                <ListItem
                  key={index}
                  sx={{
                    justifyContent:
                      msg.role === "user" ? "flex-end" : "flex-start",
                    alignItems: "flex-start",
                    py: 0.5,
                  }}
                >
                  {msg.role === "dobby" && (
                    <Avatar
                      src={avatarImagePath}
                      alt="Dobby Tools Icon"
                      sx={{ marginRight: 1, width: 24, height: 24 }}
                    />
                  )}
                  <Paper
                    sx={{
                      p: 0.75,
                      maxWidth: msg.role === "user" ? "80%" : "75%",
                      marginLeft: msg.role === "dobby" ? "0px" : 0,
                      backgroundColor:
                        msg.role === "user" ? "#e3f2fd" : "#ffffff",
                      borderRadius:
                        msg.role === "user"
                          ? "12px 12px 4px 12px"
                          : "12px 12px 12px 4px",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                    }}
                  >
                    <Typography
                      component="div"
                      variant="body2"
                      sx={{
                        wordBreak: "break-word",
                        textAlign: msg.role === "user" ? "right" : "left",
                        fontSize: "0.85rem",
                      }}
                    >
                      {msg.role === "dobby" && msg.sources
                        ? renderMessageWithCitations(
                            msg.content,
                            msg.sources,
                            handleSourceClick,
                            handleDownloadAttachment,
                          )
                        : msg.content}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{
                        fontSize: "0.65rem",
                        color: "text.secondary",
                        textAlign: msg.role === "user" ? "right" : "left",
                        display: "block",
                        mt: 0.5,
                      }}
                    >
                      {msg.timestamp.toLocaleTimeString([], {
                        hour: "2-digit",
                        minute: "2-digit",
                      })}
                    </Typography>
                  </Paper>
                </ListItem>
              ))}
              {isLoading && (
                <ListItem sx={{ justifyContent: "center", py: 1 }}>
                  <CircularProgress size={20} />
                </ListItem>
              )}
              <div ref={messagesEndRef} />
            </List>
          </Box>
        </Collapse>

        {/* Input Area - Fixed height to prevent animation issues */}
        <Box
          className="dobby-chat-input-wrapper"
          onClick={handleWrapperClick}
          sx={{
            height: "50px",
            p: 1,
            borderTop:
              messages.length > 0 && !isCollapsed
                ? "1px solid #e0e0e0"
                : "none",
            backgroundColor: "#fff",
            display: "flex",
            alignItems: "center",
            borderBottomLeftRadius: "16px",
            borderBottomRightRadius: "16px",
            cursor: isCollapsed && messages.length > 0 ? "pointer" : "default",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              width: "100%",
            }}
          >
            {/* Show loading indicator when collapsed and loading */}
            {isCollapsed && isLoading && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  pl: 4,
                  flex: 1,
                }}
              >
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Answering...
                </Typography>
              </Box>
            )}
            <TextField
              fullWidth
              multiline={false}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={handleTextFieldClick}
              placeholder="Ask Dobby..."
              disabled={isLoading}
              variant="outlined"
              size="small"
              sx={{
                display: isCollapsed && isLoading ? "none" : "block",
                "& .MuiOutlinedInput-root": {
                  borderRadius: "12px",
                  height: "36px",
                },
                "& .MuiOutlinedInput-input": {
                  padding: "8px 12px",
                },
              }}
            />
            {!(isCollapsed && isLoading) && (
              <Button
                variant="contained"
                color="primary"
                onClick={sendMessage}
                disabled={!input.trim() || isLoading}
                size="small"
                sx={{
                  minWidth: "36px",
                  width: "36px",
                  height: "36px",
                  borderRadius: "12px",
                  p: 0,
                }}
              >
                <SendIcon fontSize="small" />
              </Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Email Modal - only render if EmailThreadComponent is provided */}
      {EmailThreadComponent && (
        <Dialog
          open={emailModalOpen}
          onClose={() => setEmailModalOpen(false)}
          maxWidth="lg"
          fullWidth
        >
          <DialogContent>
            {selectedEmailId && (
              <EmailThreadComponent
                emailId={selectedEmailId}
                sx={{ height: "600px", maxWidth: "100%" }}
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setEmailModalOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
};

export default DobbyChatFloating;
